import { DateTime, utc, zone } from "timezonecomplete"
import { startOfDay } from "@hydrologiq/common/helpers/date"
import {
  getAssignmentEndDate,
  getAssignmentStartDate,
  getMonitoredAssignments,
  type AssignmentDateInput,
} from "@hydrologiq/shared/utils/assignmentDates"

/**
 * Pure time-range model for Timescale monitoring views.
 *
 * This module owns range types, presets and entity-bound resolution. It holds
 * no React state, writes no preferences, makes no GraphQL requests and reads
 * nothing from the browser: the current time and the browser IANA timezone are
 * always supplied by the caller.
 */

// ---------------------------------------------------------------------------
// Presets
// ---------------------------------------------------------------------------

export const TIMESCALE_PERIOD_VALUES = [
  "1h", "3h", "6h", "12h", "24h", "3d", "7d", "30d", "90d", "6mo", "12mo", "all",
] as const
export type TimescalePeriodValue = (typeof TIMESCALE_PERIOD_VALUES)[number]

const TIMESCALE_PERIOD_LABELS: Record<TimescalePeriodValue, string> = {
  "1h": "Last 1 hour",
  "3h": "Last 3 hours",
  "6h": "Last 6 hours",
  "12h": "Last 12 hours",
  "24h": "Last 24 hours",
  "3d": "Last 3 days",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  "6mo": "Last 6 months",
  "12mo": "Last 12 months",
  all: "Full range",
}

/** Retained relative presets in display order, each with its label. */
export const TIMESCALE_PERIOD_PRESETS: { value: TimescalePeriodValue; label: string }[] =
  TIMESCALE_PERIOD_VALUES.map((value) => ({ value, label: TIMESCALE_PERIOD_LABELS[value] }))

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TimeRangeMode = "relative" | "custom"
export type EntityActivity = "active" | "ended"

/**
 * Source data for an Asset's or System's range bounds.
 *
 * `endedAt` is only meaningful for an ended entity. A missing `earliestAllowedStart`
 * means the entity has no lower bound.
 */
export interface EntityBoundarySource {
  earliestAllowedStart?: DateTime
  activity: EntityActivity
  endedAt?: DateTime
}

export interface ResolvedEntityBounds {
  earliestAllowedStart?: DateTime
  latestAllowedEnd: DateTime
  effectiveEnd: DateTime
  activity: EntityActivity
}

export interface ParseError {
  code: "incomplete" | "invalid"
  message: string
}

export type ParseResult = { value: DateTime } | { error: ParseError }

export interface CustomDraft {
  /** Browser-local `datetime-local` value: `YYYY-MM-DDTHH:mm`. */
  from: string
  /** Browser-local `datetime-local` value: `YYYY-MM-DDTHH:mm`. */
  to: string
}

export interface RelativeSelection {
  mode: "relative"
  preset: TimescalePeriodValue
}

export interface CustomSelection {
  mode: "custom"
  draft: CustomDraft
}

export type StagedSelection = RelativeSelection | CustomSelection

/** Canonical UTC range used by graph requests. */
export interface AppliedRange {
  mode: TimeRangeMode
  preset?: TimescalePeriodValue
  startUtc: DateTime
  endUtc: DateTime
}

export interface NormaliseError {
  field: "from" | "to" | "range" | "bounds"
  code: "incomplete" | "invalid" | "invalid_bounds"
  message: string
}

export interface NormaliseResult {
  applied?: AppliedRange
  canonicalDraft?: CustomDraft
  corrected: boolean
  errors: NormaliseError[]
}

// ---------------------------------------------------------------------------
// Bound resolution
// ---------------------------------------------------------------------------

/**
 * Resolve the upper bounds for an entity at `now`.
 *
 * An ended entity with a usable `endedAt` uses it for both `effectiveEnd` and
 * `latestAllowedEnd`. Any other source, including an "ended" source with no
 * `endedAt`, is active and uses `now`. A missing `earliestAllowedStart` stays
 * unbounded; it is never fabricated.
 */
export function resolveEntityBounds(source: EntityBoundarySource, now: DateTime): ResolvedEntityBounds {
  const { earliestAllowedStart, endedAt } = source

  if (source.activity === "ended" && endedAt) {
    return { earliestAllowedStart, latestAllowedEnd: endedAt, effectiveEnd: endedAt, activity: "ended" }
  }

  return { earliestAllowedStart, latestAllowedEnd: now, effectiveEnd: now, activity: "active" }
}

// ---------------------------------------------------------------------------
// Bound derivation
// ---------------------------------------------------------------------------

/** UTC `DateTime` for an ISO string, or `undefined` when the value is empty or unparsable. */
function parseUtcInstant(value?: string | null): DateTime | undefined {
  if (!value) return undefined
  try {
    return new DateTime(value, utc()).toZone(utc())
  } catch {
    return undefined
  }
}

function toBoundarySource(start?: DateTime, end?: DateTime): EntityBoundarySource {
  return end
    ? { earliestAllowedStart: start, activity: "ended", endedAt: end }
    : { earliestAllowedStart: start, activity: "active" }
}

/**
 * Asset bounds from assignments: the earliest monitored assignment start is the
 * lower bound, and the latest assignment end marks an ended Asset. An active
 * assignment, or no assignment end, leaves the Asset active.
 */
export function boundarySourceFromAssignments(
  assignments: AssignmentDateInput[],
  monitoredIds?: (string | null)[] | null
): EntityBoundarySource {
  const monitored = getMonitoredAssignments(assignments, monitoredIds)
  return toBoundarySource(
    parseUtcInstant(getAssignmentStartDate(monitored)),
    parseUtcInstant(getAssignmentEndDate(monitored))
  )
}

/**
 * System bounds from installation dates. An installation end marks an ended
 * System; a missing end leaves it active and a missing start leaves it unbounded.
 */
export function boundarySourceFromInstallation(
  startDate?: string | null,
  endDate?: string | null
): EntityBoundarySource {
  return toBoundarySource(parseUtcInstant(startDate), parseUtcInstant(endDate))
}

// ---------------------------------------------------------------------------
// Instant helpers
// ---------------------------------------------------------------------------

const MS_MINUTE = 60 * 1000
const MS_HOUR = 60 * MS_MINUTE
const MS_DAY = 24 * MS_HOUR

/** Minimum Custom range length, and the minimum corrected length. */
const MIN_CUSTOM_RANGE_MS = 5 * MS_MINUTE

const fromMillis = (ms: number): DateTime => new DateTime(ms, utc())
const floorToMinute = (ms: number): number => Math.floor(ms / MS_MINUTE) * MS_MINUTE
const ceilToMinute = (ms: number): number => Math.ceil(ms / MS_MINUTE) * MS_MINUTE

// ---------------------------------------------------------------------------
// Browser-local date-time adapters
// ---------------------------------------------------------------------------

/** Native `datetime-local` value at minute precision: `YYYY-MM-DDTHH:mm`. */
const BROWSER_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/
/** A date, or a date with part of a time: the user has not finished entering a value. */
const PARTIAL_BROWSER_DATE_TIME = /^\d{4}-\d{2}-\d{2}(T(\d{2}:?)?)?$/

const pad = (value: number, width = 2): string => String(value).padStart(width, "0")

/**
 * Parse a native `datetime-local` value as a wall-clock time in `browserTimeZone`
 * and return it as a UTC instant with seconds and milliseconds set to zero.
 *
 * Empty or part-entered values are `incomplete`. Malformed values, impossible
 * calendar values and wall-clock times skipped by a daylight-saving change are
 * `invalid`; no timestamp is guessed for them. A wall-clock time repeated by a
 * fall-back change takes the `timezonecomplete` resolution (the first occurrence).
 */
export function parseBrowserDateTime(value: string, browserTimeZone: string): ParseResult {
  const trimmed = value.trim()
  const match = BROWSER_DATE_TIME.exec(trimmed)

  if (!match) {
    return !trimmed || PARTIAL_BROWSER_DATE_TIME.test(trimmed)
      ? { error: { code: "incomplete", message: "Enter a date and time." } }
      : { error: { code: "invalid", message: "Enter a valid date and time." } }
  }

  const [year, month, day, hour, minute] = match.slice(1).map(Number)
  const timeZone = zone(browserTimeZone)

  let local: DateTime
  try {
    local = new DateTime(year, month, day, hour, minute, 0, 0, timeZone)
  } catch {
    return { error: { code: "invalid", message: "Enter a valid date and time." } }
  }

  // timezonecomplete moves a skipped wall-clock time forward rather than rejecting it.
  if (
    local.year() !== year ||
    local.month() !== month ||
    local.day() !== day ||
    local.hour() !== hour ||
    local.minute() !== minute
  ) {
    return {
      error: {
        code: "invalid",
        message: `This date and time does not exist in ${browserTimeZone} because the clocks change.`,
      },
    }
  }

  return { value: local.toZone(utc()) }
}

/** Format a UTC instant as a native `datetime-local` value in `browserTimeZone`, dropping seconds. */
export function formatBrowserDateTime(valueUtc: DateTime, browserTimeZone: string): string {
  const local = valueUtc.toZone(zone(browserTimeZone))
  return `${pad(local.year(), 4)}-${pad(local.month())}-${pad(local.day())}T${pad(local.hour())}:${pad(local.minute())}`
}

// ---------------------------------------------------------------------------
// Relative ranges
// ---------------------------------------------------------------------------

/**
 * How far before the effective end each preset starts, and whether the start
 * snaps to local midnight. Months keep the existing 180-day and 365-day
 * semantics rather than calendar-month arithmetic.
 */
const RELATIVE_PRESET_OFFSETS: Record<Exclude<TimescalePeriodValue, "all">, { ms: number; snapToDay: boolean }> = {
  "1h": { ms: MS_HOUR, snapToDay: false },
  "3h": { ms: 3 * MS_HOUR, snapToDay: false },
  "6h": { ms: 6 * MS_HOUR, snapToDay: false },
  "12h": { ms: 12 * MS_HOUR, snapToDay: false },
  "24h": { ms: MS_DAY, snapToDay: true },
  "3d": { ms: 3 * MS_DAY, snapToDay: true },
  "7d": { ms: 7 * MS_DAY, snapToDay: true },
  "30d": { ms: 30 * MS_DAY, snapToDay: true },
  "90d": { ms: 90 * MS_DAY, snapToDay: true },
  "6mo": { ms: 180 * MS_DAY, snapToDay: true },
  "12mo": { ms: 365 * MS_DAY, snapToDay: true },
}

/** Local midnight, in `browserTimeZone`, of the day containing `ms`. */
const startOfLocalDay = (ms: number, browserTimeZone: string): number =>
  startOfDay(fromMillis(ms).toZone(zone(browserTimeZone))).unixUtcMillis()

/**
 * Relative range with the semantics of `computeTimescalePeriodRange()`, except
 * that day snapping uses the supplied `browserTimeZone` rather than the ambient
 * browser zone. Clamping to the entity bounds happens after snapping, so a real
 * assignment or installation start is never moved earlier by the snap.
 */
function computeRelativeRange(
  preset: TimescalePeriodValue,
  bounds: ResolvedEntityBounds,
  now: DateTime,
  browserTimeZone: string
): { startMs: number; endMs: number } {
  const { earliestAllowedStart, effectiveEnd, latestAllowedEnd } = bounds
  const earliestMs = earliestAllowedStart?.unixUtcMillis()
  let endMs = effectiveEnd.unixUtcMillis()
  let startMs: number

  if (preset === "all") {
    let allStartMs = earliestMs ?? now.unixUtcMillis() - 365 * MS_DAY
    if (allStartMs > endMs) allStartMs = endMs - 7 * MS_DAY
    startMs = startOfLocalDay(allStartMs, browserTimeZone)
  } else {
    const { ms, snapToDay } = RELATIVE_PRESET_OFFSETS[preset]
    startMs = endMs - ms
    if (snapToDay) startMs = startOfLocalDay(startMs, browserTimeZone)
  }

  if (earliestMs !== undefined && startMs < earliestMs) startMs = earliestMs
  endMs = Math.min(endMs, latestAllowedEnd.unixUtcMillis())

  // Very narrow bounds can leave the start at or past the end: keep the
  // existing fallback of a one-hour window ending at the end.
  if (startMs >= endMs) startMs = endMs - MS_HOUR

  return { startMs, endMs }
}

// ---------------------------------------------------------------------------
// Custom ranges
// ---------------------------------------------------------------------------

/**
 * Bound a Custom interval (UTC epoch milliseconds, minute precision) to the
 * entity. The lower bound is rounded up and the upper bound down to whole
 * minutes so the result stays inside the real bounds.
 *
 * - An interval of at least five minutes after clamping is kept as is.
 * - A shorter, equal or reversed interval becomes five minutes long: the later
 *   selected endpoint, clamped to `[earliest + 5 min, latest]` (or `<= latest`
 *   without a lower bound), is the end, and the start is five minutes before it.
 * - An allowed window shorter than five minutes is returned whole.
 *
 * Returns `undefined` when the bounds leave no whole-minute window at all.
 * Graph granularity is deliberately not considered here.
 */
function boundCustomInterval(
  startMs: number,
  endMs: number,
  bounds: ResolvedEntityBounds
): { startMs: number; endMs: number } | undefined {
  const earliestMs =
    bounds.earliestAllowedStart === undefined ? undefined : ceilToMinute(bounds.earliestAllowedStart.unixUtcMillis())
  const latestMs = floorToMinute(bounds.latestAllowedEnd.unixUtcMillis())

  if (earliestMs !== undefined && earliestMs > latestMs) return undefined

  const boundedStartMs = earliestMs === undefined ? startMs : Math.max(startMs, earliestMs)
  const boundedEndMs = Math.min(endMs, latestMs)
  if (boundedEndMs - boundedStartMs >= MIN_CUSTOM_RANGE_MS) {
    return { startMs: boundedStartMs, endMs: boundedEndMs }
  }

  if (earliestMs !== undefined && latestMs - earliestMs < MIN_CUSTOM_RANGE_MS) {
    return { startMs: earliestMs, endMs: latestMs }
  }

  const laterMs = Math.min(Math.max(startMs, endMs), latestMs)
  const correctedEndMs = earliestMs === undefined ? laterMs : Math.max(laterMs, earliestMs + MIN_CUSTOM_RANGE_MS)
  return { startMs: correctedEndMs - MIN_CUSTOM_RANGE_MS, endMs: correctedEndMs }
}

const INVALID_BOUNDS_ERROR: NormaliseError = {
  field: "bounds",
  code: "invalid_bounds",
  message: "No whole-minute range is available within the available date bounds.",
}

/**
 * Normalise a browser-local Custom draft into a canonical UTC range.
 *
 * Incomplete or unparsable fields return field errors and no range; nothing is
 * invented. Otherwise the range is bounded to the entity (see
 * `boundCustomInterval`), `corrected` reports whether the canonical values
 * differ from the entered ones, and `canonicalDraft` holds the canonical values
 * in browser-local form for blur updates.
 */
export function normaliseCustomRange(
  draft: CustomDraft,
  source: EntityBoundarySource,
  now: DateTime,
  browserTimeZone: string
): NormaliseResult {
  const from = parseBrowserDateTime(draft.from, browserTimeZone)
  const to = parseBrowserDateTime(draft.to, browserTimeZone)

  if ("error" in from || "error" in to) {
    const errors: NormaliseError[] = []
    if ("error" in from) errors.push({ field: "from", ...from.error })
    if ("error" in to) errors.push({ field: "to", ...to.error })
    return { corrected: false, errors }
  }

  const selectedStartMs = from.value.unixUtcMillis()
  const selectedEndMs = to.value.unixUtcMillis()
  const interval = boundCustomInterval(selectedStartMs, selectedEndMs, resolveEntityBounds(source, now))
  if (!interval) return { corrected: false, errors: [INVALID_BOUNDS_ERROR] }

  const startUtc = fromMillis(interval.startMs)
  const endUtc = fromMillis(interval.endMs)

  return {
    applied: { mode: "custom", startUtc, endUtc },
    canonicalDraft: {
      from: formatBrowserDateTime(startUtc, browserTimeZone),
      to: formatBrowserDateTime(endUtc, browserTimeZone),
    },
    corrected: interval.startMs !== selectedStartMs || interval.endMs !== selectedEndMs,
    errors: [],
  }
}

/**
 * Default Custom draft: the hour ending at the effective end, floored to the
 * minute (the current time for an active entity, `endedAt` for an ended one).
 * The start is clamped to an available earliest start and is never bounded
 * when the entity has none.
 */
export function defaultCustomDraft(
  source: EntityBoundarySource,
  now: DateTime,
  browserTimeZone: string
): CustomDraft {
  const bounds = resolveEntityBounds(source, now)
  const endMs = floorToMinute(bounds.effectiveEnd.unixUtcMillis())
  const startMs = endMs - MS_HOUR
  const interval = boundCustomInterval(startMs, endMs, bounds) ?? { startMs, endMs }

  return {
    from: formatBrowserDateTime(fromMillis(interval.startMs), browserTimeZone),
    to: formatBrowserDateTime(fromMillis(interval.endMs), browserTimeZone),
  }
}

/**
 * Custom draft that starts from an existing range (for example the applied Relative range), so switching to Custom
 * continues from what is on screen. The range is bounded to the entity like any Custom range; when the bounds leave
 * no whole-minute window it falls back to `defaultCustomDraft`.
 */
export function customDraftFromRange(
  startUtc: DateTime,
  endUtc: DateTime,
  source: EntityBoundarySource,
  now: DateTime,
  browserTimeZone: string
): CustomDraft {
  const result = normaliseCustomRange(
    { from: formatBrowserDateTime(startUtc, browserTimeZone), to: formatBrowserDateTime(endUtc, browserTimeZone) },
    source,
    now,
    browserTimeZone
  )
  return result.errors.length === 0 && result.canonicalDraft
    ? result.canonicalDraft
    : defaultCustomDraft(source, now, browserTimeZone)
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

/**
 * Single entry point for turning a staged selection into a canonical UTC range.
 *
 * Relative selections are recalculated from the entity's effective end at `now`;
 * Custom selections are parsed and normalised. Day snapping and local-time
 * display use the supplied `browserTimeZone`. A result with `errors` carries no
 * `applied` range, so callers keep their previous one.
 */
export function normaliseSelection(
  selection: StagedSelection,
  source: EntityBoundarySource,
  now: DateTime,
  browserTimeZone: string
): NormaliseResult {
  if (selection.mode === "custom") {
    return normaliseCustomRange(selection.draft, source, now, browserTimeZone)
  }

  const { startMs, endMs } = computeRelativeRange(
    selection.preset,
    resolveEntityBounds(source, now),
    now,
    browserTimeZone
  )

  return {
    applied: { mode: "relative", preset: selection.preset, startUtc: fromMillis(startMs), endUtc: fromMillis(endMs) },
    corrected: false,
    errors: [],
  }
}
